import { useEffect, useRef } from 'react';
import { EmotionType } from '../types/emotion';
import { useSessionServices, type EmotionSequence } from '../context/SessionServicesContext';
import { logger } from '../utils/logger';

interface EmotionHistoryEntry {
  timestamp: number;
  emotion: EmotionType;
}

interface EmotionSequenceState {
  currentEmotion: EmotionType;
  emotionHistory: EmotionType[];
  setSequence: (sequence: EmotionSequence) => void;
}

interface SessionMessage {
  speaker: 'user' | 'ai';
  text: string;
  timestamp?: number;
}

interface WebSocketState {
  aiTextResponseComplete: boolean;  // True when AI text response is complete (not audio)
  isPlaying: boolean;
  isAISpeaking: boolean;
  aiResponseInProgress: string;
  messages: SessionMessage[];
}

interface ScenarioData {
  title?: string;
  description?: string;
  category?: string;
  behavioralProfile?: any;
  positiveExpressionsOnly?: boolean;
}

interface PersonaData {
  name?: string;
}

interface SessionData {
  scenario: ScenarioData;
  persona?: PersonaData;
}

export interface UseEmotionDetectionParams {
  /** Current session status */
  sessionStatus: 'loading' | 'ready' | 'active' | 'paused' | 'finished' | 'cancelled' | 'error';
  /** Session data with scenario and persona info */
  sessionData: SessionData | null;
  /** WebSocket state for detecting AI response completion */
  websocketState: WebSocketState;
  /** Emotion sequence hook state */
  emotionSequence: EmotionSequenceState;
  /** Localized character name for context */
  localizedCharacterName: string | null;
  /** Callback to update emotion history */
  setEmotionHistory: React.Dispatch<React.SetStateAction<EmotionHistoryEntry[]>>;
}

/**
 * Hook that handles emotion detection when AI finishes speaking.
 * 
 * Analyzes the conversation and updates the avatar's emotion based on
 * the AI's last message and conversation context.
 * 
 * KEY FIX: Emotion analysis is triggered as soon as the AI TEXT response
 * completes (aiTextResponseComplete === true), NOT when audio playback
 * finishes. The full AI text is available in `aiResponseInProgress` at
 * that point (held by the conversation machine's `aiFinishing` state).
 * This fires the emotion LLM request in parallel with audio playback,
 * so the new expression is ready by the time the avatar stops speaking —
 * eliminating the multi-second delay where the avatar kept the previous
 * expression while speaking a long response.
 * 
 * @example
 * useEmotionDetection({
 *   sessionStatus,
 *   sessionData,
 *   websocketState: websocketSession.state,
 *   emotionSequence,
 *   localizedCharacterName,
 *   setEmotionHistory
 * });
 */
export function useEmotionDetection({
  sessionStatus,
  sessionData,
  websocketState,
  emotionSequence,
  localizedCharacterName,
  setEmotionHistory
}: UseEmotionDetectionParams): void {
  const { analyzeEmotions } = useSessionServices();

  // Track the text of the AI response we've already analyzed to prevent duplicate analysis.
  // KEY FIX: We track by TEXT, not by message count. The AI message is only added to
  // messages[] AFTER audio playback finishes (see conversationMachine.ts → aiFinishing
  // state → finalizeAIResponse). But the full text is available in aiResponseInProgress
  // as soon as aiTextResponseComplete becomes true (storeAIResponsePending action).
  // By analyzing the text the moment it completes — not waiting for audio — the emotion
  // request is fired in parallel with audio playback, eliminating the multi-second delay
  // where the avatar kept the previous expression while speaking a long response.
  const lastAnalyzedTextRef = useRef<string>('');
  
  // Track if analysis is currently in progress to prevent concurrent calls
  const isAnalyzingRef = useRef<boolean>(false);

  useEffect(() => {
    // The full AI text is available in aiResponseInProgress as soon as the text response
    // completes (aiTextResponseComplete === true), even while audio is still playing.
    // This is the earliest point we can request the emotion for this response.
    const aiText = websocketState.aiResponseInProgress;
    
    logger.debug('[Emotion] Effect triggered:', {
      sessionStatus,
      aiTextResponseComplete: websocketState.aiTextResponseComplete,
      isAudioPlaying: websocketState.isPlaying,
      isAISpeaking: websocketState.isAISpeaking,
      aiResponseInProgressLength: aiText?.length ?? 0,
      lastAnalyzedTextLength: lastAnalyzedTextRef.current.length,
      isAnalyzing: isAnalyzingRef.current,
    });

    if (sessionStatus !== 'active' || !websocketState.aiTextResponseComplete) {
      return;
    }
    
    // CRITICAL FIX: Only analyze if we have NEW AI text that we haven't analyzed yet.
    // We compare against the exact text we last analyzed, so the same response is never
    // analyzed twice — regardless of whether messages[] has been finalized yet.
    if (!aiText || aiText.trim().length === 0) {
      logger.debug('[Emotion] ⏭️ No AI text available yet, skipping');
      return;
    }

    if (aiText === lastAnalyzedTextRef.current) {
      logger.debug('[Emotion] ⏭️ Already analyzed this text, skipping');
      return;
    }
    
    // Prevent concurrent analysis calls
    if (isAnalyzingRef.current) {
      logger.debug('[Emotion] ⏳ Analysis already in progress, skipping');
      return;
    }
    
    // Must have sessionData to get scenario context
    if (!sessionData) {
      console.warn('[Emotion] Cannot analyze - no sessionData');
      return;
    }
    
    // Build the transcript for context. The current AI text may not yet be in messages[]
    // (it's held in aiResponseInProgress during aiFinishing), so we append it manually
    // to give the emotion service the full conversation context including this response.
    const existingMessages = websocketState.messages.map(msg => ({
      speaker: msg.speaker,
      text: msg.text,
      timestamp: msg.timestamp ?? Date.now()
    }));
    
    // If the latest AI message in messages[] doesn't match the current aiText, it means
    // the message hasn't been finalized yet (audio still playing). Append it so the
    // emotion analysis sees the complete conversation.
    const lastMsg = existingMessages[existingMessages.length - 1];
    const fullTranscript = (lastMsg && lastMsg.speaker === 'ai' && lastMsg.text === aiText)
      ? existingMessages
      : [...existingMessages, { speaker: 'ai' as const, text: aiText, timestamp: Date.now() }];
    
    // Mark that we're about to analyze this text - do this BEFORE the async call
    // to prevent any re-renders from triggering duplicate analysis
    lastAnalyzedTextRef.current = aiText;
    isAnalyzingRef.current = true;
    
    logger.debug('[Emotion] 🎯 AI text complete, analyzing emotions (in parallel with audio playback)');
    logger.debug('[Emotion] AI text length:', aiText.length);
    logger.debug('[Emotion] Audio state when starting analysis:', {
      isAudioPlaying: websocketState.isPlaying,
      isAISpeaking: websocketState.isAISpeaking,
    });
    
    // Analyze conversation and get emotion sequence
    const runEmotionAnalysis = async () => {
      try {
        // Call emotion analysis service
        const sequence = await analyzeEmotions({
          lastAIMessage: aiText,
          fullTranscript,
          scenarioContext: {
            title: sessionData.scenario.title ?? 'Untitled',
            description: sessionData.scenario.description ?? '',
            characterName: localizedCharacterName || sessionData.persona?.name || 'Character',
            category: sessionData.scenario.category,
            behavioralProfile: sessionData.scenario.behavioralProfile,
            positiveExpressionsOnly: sessionData.scenario.positiveExpressionsOnly
          },
          currentEmotion: emotionSequence.currentEmotion,
          emotionHistory: emotionSequence.emotionHistory
        });
        
        logger.debug('[Emotion] ✅ Received emotion for AI text');
        
        // Store and apply emotion (applied immediately in setSequence)
        emotionSequence.setSequence(sequence);
        
        // Track emotion in history for evaluation
        setEmotionHistory(prev => [...prev, {
          timestamp: Date.now(),
          emotion: sequence.emotion
        }]);
        
        // Emotion is now applied and will persist until next AI response
        logger.debug('[Emotion] 🎬 Applied emotion:', sequence.emotion);
        
      } catch (error) {
        console.error('[Emotion] ❌ Failed to analyze emotions:', error);
        console.warn('[Emotion] ⚠️ Continuing with current emotion - session not interrupted');
        // Don't kill the session - just keep current emotion
        // Avatar will retry emotion analysis on next AI response
      } finally {
        isAnalyzingRef.current = false;
      }
    };
    
    runEmotionAnalysis();
  }, [
    websocketState.aiTextResponseComplete,
    websocketState.aiResponseInProgress, // The full AI text — available as soon as text completes
    sessionStatus,
    sessionData,
    emotionSequence.currentEmotion, // Only the primitive values we need
    emotionSequence.emotionHistory, // Array of primitives
    emotionSequence.setSequence, // Stable callback (from useCallback)
    localizedCharacterName,
    setEmotionHistory
  ]);
}
